import React from 'react'
import { NavLink } from 'react-router-dom'
import { useReactiveVar, useMutation } from '@apollo/client/react'
import { user, isLoggingOut } from '@cache'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import Logo from '@components/logo/logo'
import Navbar from 'react-bootstrap/Navbar'
import Nav from 'react-bootstrap/Nav'
import User from '@utils/storage/user'
import { toast } from 'react-toastify'
import ToastMessage from '@components/ToastMessage/ToastMessage'
import LOGOUT from '@graphql/mutations/logout'
import redirect from '@utils/redirect'
import { t } from '@utils/translations'

const ADMIN_SIGN_OUT_PATH = '/admin_users/sign_out'

function Navigation() {
  const currentUser = useReactiveVar(user)

  const [logout] = useMutation(LOGOUT)

  // Build the Admin PCI front-channel logout URL. We reuse the OAuth host from
  // Settings and swap the path to the Admin sign-out endpoint.
  const buildAdminLogoutUrl = () => {
    const logoutUrl = new URL(Settings.OAUTH_AUTHENTICATION_URL)
    logoutUrl.pathname = ADMIN_SIGN_OUT_PATH

    return logoutUrl.toString()
  }

  const handleLogout = async () => {
    isLoggingOut(true)

    try {
      // Clears the Onboarder-side session cookie on the backend.
      await logout()
    } catch (error) {
      toast(<ToastMessage message={error?.message || t('messages.logoutError')} variant='danger' />)
      isLoggingOut(false)
      return
    }

    sessionStorage.removeItem('codeVerifier')
    User.clear()

    // Front-channel logout: navigate the browser (top-level) to the Admin PCI
    // sign-out endpoint. Unlike a cross-origin background request, a top-level
    // navigation is first-party to Admin, so the Admin session cookie is sent
    // and Warden is actually signed out. Admin then revokes the SSO tokens and
    // redirects the browser to its own login screen. This terminates the SSO
    // session so `/oauth/authorize` no longer silently re-issues a code, which
    // is what previously caused the immediate re-login.
    redirect(buildAdminLogoutUrl())
  }

  if (!currentUser) { return null }

  return (
    <Navbar aria-label='Main navigation' variant='dark'>
      <Logo />
      <Nav className='me-auto'>
        <Nav.Item>
          <NavLink to='/applications' className='nav-link'>Applications</NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to='/tokens' className='nav-link'>Tokens</NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to='/operations' className='nav-link'>Operations</NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to='/transactions' className='nav-link'>Transactions</NavLink>
        </Nav.Item>
        <Nav.Item>
          <NavLink to='/requests' className='nav-link'>Requests</NavLink>
        </Nav.Item>
      </Nav>
      <Navbar.Toggle />
      <Navbar.Collapse className='justify-content-end'>
        <Navbar.Text>
          <Button
            className='logout'
            variant='link'
            onClick={handleLogout}
          >
            Logout
            <Badge bg='primary'>{currentUser.login}</Badge>
          </Button>
        </Navbar.Text>
      </Navbar.Collapse>
    </Navbar>
  )
}

export default Navigation
